import { Component, ElementRef, inject, OnInit, ViewChild } from "@angular/core";
import { AgGridAngular } from "ag-grid-angular";
import { EditInputComponent } from "../_shared/edit-input/edit-input.component";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { DecimalPipe, NgTemplateOutlet } from "@angular/common";
import { NgxMapboxGLModule } from "ngx-mapbox-gl";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  FullProjectHash,
  ProjectDeadlines,
  ProjectUpdateParams,
  EditorPage,
  EditorPageCreateParams,
} from "../../_models/project.interface";
import { ArticleService, CustomerService, ProjectService, UserService } from "../../_services";
import {
  faArrowLeft,
  faCheck,
  faCheckCircle,
  faCog,
  faIndustry,
  faEdit,
  faInfoCircle,
  faTrash,
  faUpload,
  faUser,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { ArticleTemplate, Customer, Technician } from "../../_models";
import { AG_GRID_LOCALE_DE } from "../../_helpers/aggrid.locale.de";
import {
  Column,
  GridApi,
  IRowNode,
  RowClassRules,
  SideBarDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-enterprise";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridThemeService } from "../../_services/ag-grid-theme.service";
import { APTicket } from "../../_models/ticket.interface";
import { ProjectEditTicketAttributesComponent } from "./project-edit-ticket-attributes/project-edit-ticket-attributes.component";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { MailTemplate } from "../../_models/mail-template.interface";
import { AddNewMailTemplateModalComponent } from "./modal-components/add-new-mail-template-modal/add-new-mail-template-modal.component";
import { EditMailTemplateComponent } from "./modal-components/edit-mail-template/edit-mail-template.component";
import { TooltipModule } from "@cloudfactorydk/ng2-tooltip-directive";
import { EditProjectDeadlinesModalComponent } from "./modal-components/edit-project-deadlines-modal/edit-project-deadlines-modal.component";
import { MatProgressBar, MatProgressBarModule } from "@angular/material/progress-bar";
import { FullProjectTechnician } from "../../_models/project-technician.interface";
import { EditProjectTechnicianModalComponent } from "./modal-components/edit-project-technician-modal/edit-project-technician-modal.component";
import { PATimeControl } from "../planning-assistant/singletons/pa-time-control";
import {
  CheckboxInputType,
  AgGridFormCellComponent,
  FormGroupContext,
  InputType,
  SelectInputType,
} from "../_shared/ag-grid-renderer/ag-grid-form-cell.component";
import { APStore } from "../../_models/store.interface";
import { TechnicianWarehouseHash } from "../../_models/technician.interface";
import { ProjectTechnicianService } from "../../_services/project-technician.service";
import { ProjectStoreService } from "../../_services/project-store.service";
import { EditorJsComponent } from "../_shared/editor-js/editor-js.component";
import { environment } from "../../../environments/environment";
import { OutputData } from "@editorjs/editorjs";
import { AddNewMaterialRequirementModalComponent } from "./modal-components/add-new-material-requirement-modal/add-new-material-requirement-modal.component";
import { EditMaterialRequirementModalComponent } from "./modal-components/edit-material-requirement-modal/edit-material-requirement-modal.component";
import {
  AgGridFAActionSymbolRenderer,
  FaActionIcon,
} from "../_shared/ag-grid-renderer/ag-grid-fa-action-symbols.component";
import { MaterialRequirement } from "../../_models/material-requirement.interface";
import { MaterialRequirementService } from "../../_services/material-article.service";
import { DismantlingArticlesTabComponent } from "./dismantling-articles-tab/dismantling-articles-tab.component";
import { StagingConfigTabComponent } from "./staging-config-tab/staging-config-tab.component";

export interface ProjectTechnicianFormCellInput {
  selected_input: CheckboxInputType;
  name: string;
  bin_input: SelectInputType;
  user_id: number;
}

export interface ProjectStoreFormCellInput {
  selected_input: CheckboxInputType;
  store: APStore;
}

@Component({
  selector: "hawk-project-show",
  imports: [
    AgGridAngular,
    EditInputComponent,
    FaIconComponent,
    FormsModule,
    NgTemplateOutlet,
    NgxMapboxGLModule,
    RecordNavigationComponent,
    RouterLink,
    ProjectEditTicketAttributesComponent,
    AddNewMailTemplateModalComponent,
    EditMailTemplateComponent,
    TooltipModule,
    EditProjectDeadlinesModalComponent,
    MatProgressBar,
    MatProgressBarModule,
    EditProjectTechnicianModalComponent,
    ReactiveFormsModule,
    EditorJsComponent,
    AddNewMaterialRequirementModalComponent,
    EditMaterialRequirementModalComponent,
    DecimalPipe,
    DismantlingArticlesTabComponent,
    StagingConfigTabComponent,
  ],
  templateUrl: "./project-show.component.html",
  standalone: true,
  styleUrls: ["./project-show.component.scss"],
  host: {
    class: "app-page",
  },
})
export class ProjectShowComponent implements OnInit {
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  pseudoTechnicianNames: string[] = [
    "offene Planung",
    "Bentomax Bereitschaft",
    "nicht vergeben",
    "Kein Einsatz notwendig",
    "Materialerfassung Kiel",
  ];

  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faTrash = faTrash;
  protected readonly faCheck = faCheck;

  project: FullProjectHash;
  startDate: Date;
  endDate: Date;
  projectIsDeletable: boolean = false;

  customers: Customer[] = [];

  materialRequirementData: (MaterialRequirement & { actions: FaActionIcon[] })[] = [];

  tickets: APTicket[] = [];
  openTicketCount: number = 0;
  closedTicketCount: number = 0;
  statusCounts: { name: string; id: number; count: number; closed?: boolean }[] = [];
  priorityCounts: { name: string; id: number; count: number }[] = [];
  taskCounts: { name: string; id: number; count: number }[] = [];
  totalTaskCount: number = 0;

  dispatchers: Technician[] = [];
  selectedDispatcherIds: number[] = [];
  technicians: TechnicianWarehouseHash[] = [];
  selectedTechnicianIds: number[] = [];

  projectTechnicianFormCellInputs: ProjectTechnicianFormCellInput[] = [];
  projectStoreFormCellInputs: ProjectStoreFormCellInput[] = [];

  // Project type
  projectBusinessType = {
    id: 1,
    name: "Projektgeschäft",
  };
  maintenanceType = {
    id: 2,
    name: "Wartung",
  };
  selectedProjectTypeID?: number = null;
  projectTypeTranslations = {
    PROJECT_BUSINESS: this.projectBusinessType,
    MAINTENANCE: this.maintenanceType,
  };

  selectableProjectTypes = [this.projectBusinessType, this.maintenanceType];

  editName: boolean = false;
  editActive: boolean = false;
  editDismantlingProject: boolean = false;
  editCustomer: boolean = false;
  editType: boolean = false;
  editDescription: boolean = false;
  editCostCenter: boolean = false;
  editCutOffTime: boolean = false;
  editStores: boolean = false;
  editDispatchers: boolean = false;
  editTelephoneDispatching: boolean = false;
  editContactData: boolean = false;
  editContactMail: boolean = false;
  editContact: boolean = false;
  editEmail: boolean = false;
  editTicketAttributes: boolean = false;
  editTechnicians: boolean = false;
  editProductSelection: boolean = false;
  editSendOperationNew: boolean = false;
  editSendOperationUpdate: boolean = false;
  editSendOperationDelete: boolean = false;
  editStart: boolean = false;
  editEnd: boolean = false;
  editDocumentation: boolean = false;

  addMailTemplateForm: UntypedFormGroup;
  editMailTemplateForm: UntypedFormGroup;
  editDeadlineTemplate: UntypedFormGroup;
  editProjectTechnicianForm: UntypedFormGroup;

  addMaterialRequirementForm: UntypedFormGroup;
  editMaterialRequirementForm: UntypedFormGroup;

  // UI-Status fürs Speichern der Stores
  isSavingStores: boolean = false;
  storeSaveProgress: number = 0; // wie viele Requests schon erledigt
  storeSaveTotal: number = 0; // wie viele Requests insgesamt

  // ag grid
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public oldTheme = inject(AgGridThemeService).getOldTheme();
  protected readonly locale = AG_GRID_LOCALE_DE;
  private ticketGridApi!: GridApi;
  private materialRequirementApi!: GridApi;
  private projectTechnicianGridApi!: GridApi;
  private projectStoreGridApi!: GridApi;

  public rowClassRules: RowClassRules = {};
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  public ticketColDefs: ColDef[] = [
    {
      field: "order_nr",
      headerName: "Nr.",
      width: 160,
      cellClass: "hover_link_cell",
      cellRenderer: this.orderNrRenderer,
    },
    {
      field: "priority.name",
      headerName: "Priorität",
      width: 240,
    },
    {
      field: "store",
      headerName: "Filiale",
      width: 360,
      cellClass: "hover_link_cell",
      cellRenderer: this.storeRenderer,
    },
  ];

  public materialRequirementColDefs: ColDef[] = [
    {
      field: "amount",
      headerName: "Menge",
      maxWidth: 120,
    },
    {
      field: "article_template.description",
      headerName: "Template-Beschreibung",
    },
    {
      field: "actions",
      headerName: "Aktionen",
      maxWidth: 120,
      cellRenderer: AgGridFAActionSymbolRenderer,
      sortable: false,
      suppressFiltersToolPanel: true,
      filter: false,
    },
  ];

  public projectTechnicianColDefs: ColDef[] = [
    {
      headerName: "Aktiviert",
      field: "selected_input",
      cellRenderer: "checkboxFormCell",
      width: 130,
      suppressSizeToFit: true,
    },
    { headerName: "Techniker", field: "name" },
    { headerName: "Lagerplatz", field: "bin_input", cellRenderer: "selectFormCell" },
  ];

  public projectStoreColDefs: ColDef[] = [
    {
      headerName: "Verknüpft",
      field: "selected_input",
      cellRenderer: "checkboxFormCell",
      width: 140,
      suppressSizeToFit: true,
    },
    { headerName: "Nr", field: "store.nr", width: 160 },
    { headerName: "Name", field: "store.name", width: 160 },
    { headerName: "Adresse", field: "store.address", width: 320 },
  ];

  projectTechnicianForm = new UntypedFormGroup({});
  projectStoreForm = new UntypedFormGroup({});

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ["columns", "filters"],
  };

  public defaultColDef: ColDef = {
    filter: true,
  };

  public rowSelection: "single" | "multiple" = "single";
  public selectedTab: string = "data";
  private customerStores: APStore[] = [];
  public articleTemplates: ArticleTemplate[] = [];

  editorJsImgEndpoint: string;
  shownEditorPage: EditorPage;
  editEditorPages = false;

  constructor(
    private projectService: ProjectService,
    private customerService: CustomerService,
    private userService: UserService,
    private projectTechnicianService: ProjectTechnicianService,
    private projectStoreService: ProjectStoreService,
    private materialRequirementService: MaterialRequirementService,
    private articleService: ArticleService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.editorJsImgEndpoint = environment.apiURL + `projects/${id}/editorjs_image`;

    // Query-Parameter: Tab direkt öffnen (z.B. ?tab=dismantling-articles)
    const tabParam = this.route.snapshot.queryParamMap.get("tab");
    if (tabParam) {
      this.selectedTab = tabParam;
    }

    this.loadProject().then((_) => {
      this.loadCustomers();
      this.loadCustomerStores();
      this.loadDispatcher();
      this.loadTechnicians();
      this.loadTickets();
      this.loadArticleTemplates();
    });
  }

  loadArticleTemplates(): void {
    this.articleService.loadArticleTemplates().subscribe(
      (data) => {
        this.articleTemplates = data;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  loadCustomers(): void {
    this.customerService.loadCustomers().subscribe(
      (customers) => {
        this.customers = customers.sort((c1, c2) => (c1.name.toLowerCase() < c2.name.toLowerCase() ? -1 : 1));
      },
      (err) => {
        console.log(err);
      },
    );
  }

  async loadProject(): Promise<void> {
    await new Promise<void>((resolve) => {
      const id = this.route.snapshot.paramMap.get("id");
      this.projectService.getFullProject(id).subscribe(
        (data) => {
          this.updateProjectData(data);
          resolve();
        },
        (error) => {
          console.log(error);
          resolve();
        },
      );
      this.updateDeletable();
    });
  }

  updateDeletable(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.projectService.projectIsDeletable(id).subscribe(
      (data) => {
        this.projectIsDeletable = data.deletable;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  loadDispatcher(): void {
    this.userService.getDispatcher().subscribe(
      (data) => {
        this.dispatchers = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  loadTechnicians(): void {
    this.userService.getUsersWarehouseHashes().subscribe(
      (data) => {
        this.technicians = data
          .filter((t) => !this.pseudoTechnicianNames.includes(t.firstname + " " + t.lastname))
          .sort((t_a: TechnicianWarehouseHash, t_b: TechnicianWarehouseHash) => {
            if (
              (t_a.is_bentomax_technician && t_b.is_bentomax_technician) ||
              (!t_a.is_bentomax_technician && !t_b.is_bentomax_technician)
            ) {
              return t_a.lastname < t_b.lastname ? -1 : 1;
            } else {
              return t_a.is_bentomax_technician ? -1 : 1;
            }
          });
        this.generateProjectTechnicianFormCellInputs();
      },
      (error) => {
        console.log(error);
      },
    );
  }

  loadTickets(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.projectService.getTickets(id).subscribe(
      (data) => {
        this.tickets = data;
        this.openTicketCount = data.filter((t) => !t.status?.closed).length;
        this.closedTicketCount = data.filter((t) => t.status?.closed).length;
        this.updateTicketCounts();
      },
      (error) => {
        console.log(error);
      },
    );
  }

  loadCustomerStores(): void {
    this.customerService.loadAPCustomerStores(this.project.customer.id).subscribe(
      (data) => {
        this.customerStores = data;
        this.generateStoreFormCellInputs();
      },
      (err) => console.log(err),
    );
  }

  updateTicketCounts(): void {
    this.statusCounts = this.countNames(
      this.tickets
        .filter((t) => t.status)
        .map((t) => {
          return { name: t.status.name, id: t.status.id, closed: t.status.closed };
        }),
    );
    this.priorityCounts = this.countNames(
      this.tickets
        .filter((t) => t.priority)
        .map((t) => {
          return { name: t.priority.name, id: t.priority.id };
        }),
    );
    this.taskCounts = this.countNames(
      this.tickets
        .filter((t) => t.tasks)
        .map((t) => {
          return t.tasks;
        })
        .flat(),
    );
    this.totalTaskCount = this.taskCounts.reduce((total: number, task_count) => total + task_count.count, 0);
  }

  countNames(
    count_list: { name: string; id: number; closed?: boolean }[],
  ): { name: string; id: number; count: number; closed?: boolean }[] {
    const counts: { name: string; id: number; count: number; closed?: boolean }[] = [];

    for (const item of count_list) {
      const count = counts.find((c) => c.id == item.id);
      if (!count) {
        counts.push({
          name: item.name,
          id: item.id,
          count: 1,
          closed: item.closed,
        });
      } else {
        count.count += 1;
      }
    }

    return counts.sort((ca, cb) => (ca.count < cb.count ? 1 : -1));
  }

  updateProjectData(project: FullProjectHash) {
    this.project = project;
    this.selectedProjectTypeID = this.projectTypeTranslations[project.type].id;
    this.selectedTechnicianIds = project.project_technicians.map((t) => t.user_id);
    this.selectedDispatcherIds = project.dispatchers.map((t) => t.id);
    this.startDate = this.getStartDate();
    this.endDate = this.getEndDate();

    this.autoSelectEditorPage();
    this.updateMaterialRequirementData();

    this.generateProjectTechnicianFormCellInputs();
    this.generateStoreFormCellInputs();
  }

  autoSelectEditorPage(): void {
    if (!this.editEditorPages && !this.shownEditorPage) {
      this.shownEditorPage = this.project.editor_pages ? this.project.editor_pages[0] : null;
    } else if (this.shownEditorPage) {
      this.shownEditorPage = this.project.editor_pages
        ? this.project.editor_pages.find((p) => p.id === this.shownEditorPage.id)
        : null;
    }
    if (!this.shownEditorPage) this.editEditorPages = true;
  }

  updateMaterialRequirementData(): void {
    this.materialRequirementData = this.project.material_requirements.map((mr) => {
      return {
        project_id: mr.project_id,
        id: mr.id,
        article_template: mr.article_template,
        amount: mr.amount,
        actions: [
          {
            action: () => {
              this.openEditMaterialRequirementForm(mr);
            },
            icon: faEdit,
            tooltip: "bearbeiten",
            css_classes: "",
          },
          {
            action: () => {
              this.openDeleteMaterialRequirementDialog(mr);
            },
            icon: faTrash,
            tooltip: "löschen",
            css_classes: "",
          },
        ],
      };
    });
  }

  saveName($event: string) {
    this.saveProjectValues({ name: $event }).then((success) => {
      if (success) this.editName = false;
    });
  }

  saveActive($event: boolean) {
    this.saveProjectValues({ active: $event }).then((success) => {
      if (success) this.editActive = false;
    });
  }

  saveDismantlingProject($event: boolean) {
    this.saveProjectValues({ is_dismantling_project: $event }).then((success) => {
      if (success) this.editDismantlingProject = false;
    });
  }

  saveDescription($event: string) {
    this.saveProjectValues({ description: $event }).then((success) => {
      if (success) this.editDescription = false;
    });
  }

  saveCustomer($event: number) {
    this.saveProjectValues({ customer_id: $event }).then((success) => {
      if (success) this.editCustomer = false;
    });
  }

  saveType($event: number) {
    const types = ["PROJECT_BUSINESS", "MAINTENANCE"];
    this.saveProjectValues({ type: types[$event - 1] }).then((success) => {
      if (success) this.editType = false;
    });
  }

  saveCostCenter($event: string) {
    this.saveProjectValues({ cost_center: $event }).then((success) => {
      if (success) this.editCostCenter = false;
    });
  }

  saveCutOffTime($event: string) {
    this.saveProjectValues({ cut_off_time: $event }).then((success) => {
      if (success) this.editCutOffTime = false;
    });
  }

  saveContact($event: string) {
    this.saveProjectValues({ contact: $event }).then((success) => {
      if (success) this.editContact = false;
    });
  }

  saveEmail($event: string) {
    this.saveProjectValues({ email: $event }).then((success) => {
      if (success) this.editEmail = false;
    });
  }

  saveProductSelection($event: boolean) {
    this.saveProjectValues({ select_products: $event }).then((success) => {
      if (success) this.editProductSelection = false;
    });
  }

  saveSendOperationNew($event: boolean) {
    this.saveProjectValues({ send_operation_new: $event }).then((success) => {
      if (success) this.editSendOperationNew = false;
    });
  }

  saveSendOperationUpdate($event: boolean) {
    this.saveProjectValues({ send_operation_update: $event }).then((success) => {
      if (success) this.editSendOperationUpdate = false;
    });
  }

  saveSendOperationDelete($event: boolean) {
    this.saveProjectValues({ send_operation_delete: $event }).then((success) => {
      if (success) this.editSendOperationDelete = false;
    });
  }

  async saveTechnicians(ids: number[]) {
    const add_technician_ids = ids.filter((id) => !this.project.project_technicians.find((t) => t.user_id == id));
    const remove_technician_ids = this.project.project_technicians
      .map((t) => t.user_id)
      .filter((pt_id) => !ids.find((id) => pt_id == id));

    await new Promise<boolean>((resolve) => {
      this.projectService
        .updateProjectTechnicians(this.project.id, { remove_technician_ids, add_technician_ids })
        .subscribe(
          (data) => {
            this.updateProjectData(data);
            resolve(true);
          },
          (error) => {
            console.log(error);
            resolve(false);
          },
        );
    }).then((success) => {
      if (success) this.editTechnicians = false;
    });
  }

  async saveDispatchers(ids: number[]) {
    const add_dispatcher_ids = ids.filter((id) => !this.project.dispatchers.find((t) => t.id == id));
    const remove_dispatcher_ids = this.project.dispatchers
      .map((t) => t.id)
      .filter((pt_id) => !ids.find((id) => pt_id == id));

    await new Promise<boolean>((resolve) => {
      this.projectService
        .updateProjectDispatchers(this.project.id, { remove_dispatcher_ids, add_dispatcher_ids })
        .subscribe(
          (data) => {
            this.updateProjectData(data);
            resolve(true);
          },
          (error) => {
            console.log(error);
            resolve(false);
          },
        );
    }).then((success) => {
      if (success) this.editDispatchers = false;
    });
  }

  saveStartDate($event: Date) {
    console.log("saving: " + $event.toString());
    const date = $event ? PATimeControl.Instance.formatDate($event) : null;
    this.saveProjectValues({ start: date }).then((success) => {
      if (success) this.editStart = false;
    });
  }

  saveEndDate($event: Date) {
    const date = $event ? PATimeControl.Instance.formatDate($event) : null;
    this.saveProjectValues({ end: date }).then((success) => {
      if (success) this.editEnd = false;
    });
  }

  saveDocumentation($event: string) {
    this.saveProjectValues({ additional_documentation: $event }).then((success) => {
      if (success) this.editDocumentation = false;
    });
  }

  saveColor(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.saveProjectValues({ color: value });
  }

  async saveProjectValues(params: ProjectUpdateParams): Promise<boolean> {
    return await new Promise<boolean>((resolve) => {
      this.projectService.updateFullProject(this.project.id, params).subscribe(
        (data) => {
          this.updateProjectData(data);
          this.updateDeletable();
          resolve(true);
        },
        (error) => {
          console.log(error);
          resolve(false);
        },
      );
    });
  }

  deleteProject() {
    this.projectService.deleteProject(this.project.id).subscribe(
      (_) => {
        this.router.navigate(["/projekt"]);
      },
      (err) => {
        console.log(err);
      },
    );
  }

  onTicketGridReady(params: GridReadyEvent) {
    this.ticketGridApi = params.api;
  }

  onProjectTechnicianGridReady(params: GridReadyEvent): void {
    this.projectTechnicianGridApi = params.api;
    this.initProjectTechnicianFormControls();
  }

  onProjectStoreGridReady(params: GridReadyEvent): void {
    this.projectStoreGridApi = params.api;

    // initial sichtbar machen
    this.syncVisibleStoreFormControls(true);

    // wenn das Grid das Modell neu rendert (Filter, Sort, QuickSearch, Data refresh)
    this.projectStoreGridApi.addEventListener("modelUpdated", () => {
      if (this.projectStoreGridApi?.isDestroyed()) return;
      this.syncVisibleStoreFormControls(true);
    });

    // wenn gescrollt wird und neue Rows ins Viewport kommen
    this.projectStoreGridApi.addEventListener("bodyScroll", () => {
      if (this.projectStoreGridApi?.isDestroyed()) return;
      this.syncVisibleStoreFormControls();
    });

    // wenn Column resize, etc. auch zu neuem Render führen kann
    this.projectStoreGridApi.addEventListener("viewportChanged", () => {
      if (this.projectStoreGridApi?.isDestroyed()) return;
      this.syncVisibleStoreFormControls();
    });
  }

  onMaterialRequirementsGridReady(params: GridReadyEvent) {
    this.materialRequirementApi = params.api;
  }

  public ticketQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.ticketGridApi.setGridOption("quickFilterText", newValue);
  }

  public projectTechnicianQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.projectTechnicianGridApi.setGridOption("quickFilterText", newValue);
  }

  public storeQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.projectStoreGridApi.setGridOption("quickFilterText", newValue);
  }

  orderNrRenderer(params: any): string {
    const id = params.data.id;
    return `<a class="hover_link" href="/a/ticket/${id}" rel="noopener">${params.value}</a>`;
  }

  storeRenderer(params: any): string {
    if (params.value) {
      const id = params.value.id;
      return `<a class="hover_link" href="/a/filiale/${id}" rel="noopener">${params.value.name}</a>`;
    } else {
      return "-";
    }
  }

  customerRenderer(params: any): string {
    const id = params.value.id;
    return `<a class="hover_link" href="/a/kunde/${id}" rel="noopener">${params.value.name}</a>`;
  }

  openAddMailTemplateForm(): void {
    this.addMailTemplateForm = this.getMailTemplateForm();
  }

  openEditMailTemplateForm(mail_template: MailTemplate): void {
    this.editMailTemplateForm = this.getMailTemplateForm(mail_template);
  }

  deleteMailTemplate(template: MailTemplate): void {
    if (confirm(`Soll das Mail Template wirklich gelöscht werden?`)) {
      this.projectService.deleteProjectMailTemplate(this.project.id, template.id).subscribe(
        (data) => {
          this.updateProjectData(data);
        },
        (error) => {
          console.log(error);
        },
      );
    }
  }

  getMailTemplateForm(template?: MailTemplate): UntypedFormGroup {
    const form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      subject: new UntypedFormControl({ value: "" }, Validators.required),
      text: new UntypedFormControl({ value: "" }, Validators.required),
      type: new UntypedFormControl({ value: "STATUS_CHANGED" }, Validators.required),
      project_id: new UntypedFormControl({ value: this.project.id }, Validators.required),
    });

    form.addControl("use_dynamic_report", new UntypedFormControl({ value: false }));

    form.controls["id"].setValue(template?.id || null);
    form.controls["subject"].setValue(template?.subject || "");
    form.controls["text"].setValue(template?.text || "");
    form.controls["type"].setValue(template ? template.type : "STATUS_CHANGED");
    form.controls["project_id"].setValue(this.project.id);
    form.controls["use_dynamic_report"].setValue(template?.use_dynamic_report || false);

    for (const key of [
      "orderNr",
      "status",
      "reportLink",
      "dateSLA",
      "operationDate",
      "tasks",
      "priority",
      "dateCreated",
      "externalOrderNr",
      "description",
      "project",
      "store",
      "address",
      "deliveryNotes",
      "contactName",
      "contactEmail",
      "contactPhone",
      "combined_report",
      "operation.date",
      "operation.status",
      "operation.technicians",
      "operation.description",
      "operation.report",
      "operation.travelTimeStart",
      "operation.travelTimeEnd",
      "operation.travelTime",
      "operation.workingTimeStart",
      "operation.workingTimeEnd",
      "operation.workingTime",
      "operation.dateTravelStart",
      "operation.dateOnSite",
      "operation.dateRepaired",
      "operation.dateFinished",
      "operation.distance",
      "operation.files",
      "operation.add_report_photo",
      "operation.dont_send_report",
    ]) {
      form.addControl(key, new UntypedFormControl({ value: false }));
      form.controls[key].setValue(
        template?.mail_fields ? !!template?.mail_fields.find((field) => field.name == key) : false,
      );
    }

    form.updateValueAndValidity();

    return form;
  }

  openAddMaterialRequirementForm(): void {
    this.addMaterialRequirementForm = this.createMaterialRequirementForm();
  }

  openEditMaterialRequirementForm(material_requirement: MaterialRequirement): void {
    this.editMaterialRequirementForm = this.createMaterialRequirementForm(material_requirement);
  }

  openEditDeadlineModal() {
    this.editDeadlineTemplate = this.getDeadlineForm(this.project);
  }

  getDeadlineForm(template?: ProjectDeadlines): UntypedFormGroup {
    const form = new UntypedFormGroup({
      use_deadline: new UntypedFormControl({ value: "" }),
      use_deadline2: new UntypedFormControl({ value: "" }),
      use_deadline3: new UntypedFormControl({ value: "" }),
      deadline_days: new UntypedFormControl({ value: "" }),
      deadline_days2: new UntypedFormControl({ value: "" }),
      deadline_days3: new UntypedFormControl({ value: "" }),
      deadline_hours: new UntypedFormControl({ value: "" }),
      deadline_hours2: new UntypedFormControl({ value: "" }),
      deadline_hours3: new UntypedFormControl({ value: "" }),
      deadline_minutes: new UntypedFormControl({ value: "" }),
      deadline_minutes2: new UntypedFormControl({ value: "" }),
      deadline_minutes3: new UntypedFormControl({ value: "" }),
      mail: new UntypedFormControl({ value: "" }),
      mail2: new UntypedFormControl({ value: "" }),
      mail3: new UntypedFormControl({ value: "" }),
      sms: new UntypedFormControl({ value: "" }),
      sms2: new UntypedFormControl({ value: "" }),
      sms3: new UntypedFormControl({ value: "" }),
    });

    form.controls["use_deadline"].setValue(template?.use_deadline || false);
    form.controls["use_deadline2"].setValue(template?.use_deadline2 || false);
    form.controls["use_deadline3"].setValue(template?.use_deadline3 || false);
    form.controls["deadline_days"].setValue(template?.deadline_days || 0);
    form.controls["deadline_days2"].setValue(template?.deadline_days2 || 0);
    form.controls["deadline_days3"].setValue(template?.deadline_days3 || 0);
    form.controls["deadline_hours"].setValue(template?.use_deadline || 0);
    form.controls["deadline_hours2"].setValue(template?.deadline_hours2 || 0);
    form.controls["deadline_hours3"].setValue(template?.deadline_hours3 || 0);
    form.controls["deadline_minutes"].setValue(template?.deadline_minutes || 0);
    form.controls["deadline_minutes2"].setValue(template?.deadline_minutes2 || 0);
    form.controls["deadline_minutes3"].setValue(template?.deadline_minutes3 || 0);
    form.controls["mail"].setValue(template?.mail || false);
    form.controls["mail2"].setValue(template?.mail2 || false);
    form.controls["mail3"].setValue(template?.mail3 || false);
    form.controls["sms"].setValue(template?.sms || false);
    form.controls["sms2"].setValue(template?.sms2 || false);
    form.controls["sms3"].setValue(template?.sms3 || false);

    form.updateValueAndValidity();

    return form;
  }

  openEditProjectTechnicianForm(project_technician: FullProjectTechnician): void {
    this.editProjectTechnicianForm = this.getProjectTechnicianForm(project_technician);
  }

  getProjectTechnicianForm(template: FullProjectTechnician): UntypedFormGroup {
    const form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      user_id: new UntypedFormControl({ value: "", disabled: true }),
      zip_area: new UntypedFormControl({ value: "" }),
      standby_time: new UntypedFormControl({ value: "" }),
      bin_id: new UntypedFormControl({ value: null }),
    });

    form.controls["id"].setValue(template.id);
    form.controls["user_id"].setValue(template.user_id);
    form.controls["zip_area"].setValue(template.zip_area);
    form.controls["standby_time"].setValue(template.standby_time);
    form.controls["bin_id"].setValue(template.bin_id);

    form.updateValueAndValidity();

    return form;
  }

  protected readonly faUser = faUser;
  protected readonly faIndustry = faIndustry;
  protected readonly faPen = faPen;
  protected readonly faInfoCircle = faInfoCircle;

  protected readonly faCheckCircle = faCheckCircle;

  selectTab(tab: string) {
    this.selectedTab = tab;
  }

  getEndDate() {
    if (this.project?.end) {
      return new Date(this.project.end);
    } else {
      return null;
    }
  }

  getStartDate() {
    if (this.project?.start) {
      return new Date(this.project.start);
    } else {
      return null;
    }
  }

  getProjectTechnicianContext(): FormGroupContext {
    return {
      formGroup: this.projectTechnicianForm,
      createKey: this.createProjectTechnicianKey,
    };
  }

  getProjectStoreContext(): FormGroupContext {
    return {
      formGroup: this.projectStoreForm,
      createKey: this.createProjectStoreKey,
    };
  }

  getAGComponents() {
    return {
      checkboxFormCell: AgGridFormCellComponent<CheckboxInputType>,
      selectFormCell: AgGridFormCellComponent<SelectInputType>,
    };
  }

  private createProjectStoreKey(column_field: string, row_node: IRowNode): string {
    return `${column_field};${row_node.data.store.id}`;
  }

  private createProjectTechnicianKey(column_field: string, row_node: IRowNode): string {
    return `${column_field};${row_node.data.user_id}`;
  }

  initProjectTechnicianFormControls() {
    if (this.projectTechnicianGridApi && !this.projectTechnicianGridApi.isDestroyed()) {
      this.createProjectTechnicianFormControls();
      this.projectTechnicianGridApi.refreshCells({ force: true });
    }
  }

  initProjectStoreFormControls() {
    if (this.projectStoreGridApi && !this.projectStoreGridApi.isDestroyed()) {
      this.createProjectStoreFormControls();
      this.projectStoreGridApi.refreshCells({ force: true });
    }
  }

  private createProjectTechnicianFormControls() {
    const columns = this.projectTechnicianGridApi.getColumns();

    const form_group = <UntypedFormGroup>this.projectTechnicianForm;

    // Prüfen ob Nodes vorhanden sind, bevor Controls gelöscht werden
    let nodeCount = 0;
    this.projectTechnicianGridApi.forEachNode(() => nodeCount++);
    if (nodeCount === 0) return;

    // clear old form group controls
    Object.keys(form_group.controls).forEach((key) => form_group.removeControl(key));

    this.projectTechnicianGridApi.forEachNode((rowNode: IRowNode, idx: number) => {
      columns
        .filter((column: Column) => column.getColDef().field !== "name")
        .forEach((column: Column<InputType>) => {
          const field = column.getColDef().field;
          const key = this.createProjectTechnicianKey(field, rowNode); // the cells will use this same createKey method
          form_group.addControl(key, new FormControl());
          const control = form_group.controls[key];
          const input = this.projectTechnicianFormCellInputs.find((cell) => cell.user_id === rowNode.data.user_id)?.[
            field
          ];
          control.setValue(input.value);
          if (input?.disabled) control.disable();
        });
    });
  }

  private createProjectStoreFormControls() {
    console.log("Gen form controls start");
    const columns = this.projectStoreGridApi.getColumns();

    const form_group = <UntypedFormGroup>this.projectStoreForm;

    // clear old form group controls
    Object.keys(form_group.controls).forEach((key) => form_group.removeControl(key));

    this.projectStoreGridApi.forEachNode((rowNode: IRowNode, idx: number) => {
      columns
        .filter((column: Column) => !["store.nr", "store.name", "store.address"].includes(column.getColDef().field))
        .forEach((column: Column<InputType>) => {
          const field = column.getColDef().field;
          const key = this.createProjectStoreKey(field, rowNode); // the cells will use this same createKey method
          form_group.addControl(key, new FormControl());
          const control = form_group.controls[key];
          const input = this.projectStoreFormCellInputs.find((cell) => cell.store.id === rowNode.data.store.id)?.[
            field
          ];
          control.setValue(input.value);
          if (input?.disabled) control.disable();
        });
    });
    console.log("Gen form controls end");
  }

  private ensureStoreFormControlsForRow(rowNode: IRowNode, set_data?: boolean): void {
    const columns = this.projectStoreGridApi.getColumns();
    const form_group = this.projectStoreForm as UntypedFormGroup;

    columns
      .filter((column: Column) => !["store.nr", "store.name", "store.address"].includes(column.getColDef().field))
      .forEach((column: Column<InputType>) => {
        const field = column.getColDef().field;
        const key = this.createProjectStoreKey(field, rowNode);

        // Falls Control schon existiert -> nur Wert syncen
        if (form_group.controls[key] && set_data) {
          const input = this.projectStoreFormCellInputs.find((cell) => cell.store.id === rowNode.data.store.id)?.[
            field
          ];

          // nur den .value in das Control schreiben
          form_group.controls[key].setValue(input?.value ?? null, { emitEvent: false });
          if (input?.disabled) form_group.controls[key].disable({ emitEvent: false });
          return;
        }

        // Control existiert noch nicht -> neu anlegen
        const ctrl = new FormControl();
        form_group.addControl(key, ctrl);

        const input = this.projectStoreFormCellInputs.find((cell) => cell.store.id === rowNode.data.store.id)?.[field];

        ctrl.setValue(input?.value ?? null, { emitEvent: false });
        if (input?.disabled) ctrl.disable({ emitEvent: false });
      });
  }

  private syncVisibleStoreFormControls(set_data?: boolean): void {
    if (!this.projectStoreGridApi || this.projectStoreGridApi.isDestroyed()) return;

    const firstIdx = this.projectStoreGridApi.getFirstDisplayedRowIndex();
    const lastIdx = this.projectStoreGridApi.getLastDisplayedRowIndex();

    // Safety: Grid kann -1 zurückgeben falls leer
    if (firstIdx == null || firstIdx < 0 || lastIdx == null || lastIdx < 0) return;

    const visibleRowNodes: IRowNode[] = [];
    for (let i = firstIdx; i <= lastIdx; i++) {
      const node = this.projectStoreGridApi.getDisplayedRowAtIndex(i);
      if (node) visibleRowNodes.push(node);
    }

    visibleRowNodes.forEach((node) => this.ensureStoreFormControlsForRow(node, set_data));
  }

  getProjectTechnicianRowNodeId(data: { data: ProjectTechnicianFormCellInput }): string {
    return data.data.user_id.toString();
  }

  getStoreRowNodeId(data: { data: ProjectStoreFormCellInput }): string {
    return data.data.store.id.toString();
  }

  protected readonly faUpload = faUpload;

  private generateProjectTechnicianFormCellInputs() {
    if (this.project) {
      this.projectTechnicianFormCellInputs = this.technicians.map((t) => {
        const pt = this.project.project_technicians.find((pt) => pt.user_id == t.id);
        return {
          name: `${t.lastname}, ${t.firstname}`,
          user_id: t.id,
          bin_input: {
            type: "select_groups",
            value: pt?.bin_id || null,
            opt_groups: t.warehouses.map((w) => {
              return {
                label: w.label,
                options: w.bins,
              };
            }),
            disabled: false,
            null_option: true,
          },
          selected_input: {
            type: "checkbox",
            value: !!pt,
          },
        };
      });
    }
  }

  activateBentomaxProjectTechnicians() {
    const form = this.projectTechnicianForm;
    const bentomax_technician_ids = this.technicians.filter((t) => t.is_bentomax_technician).map((t) => t.id);
    this.projectTechnicianGridApi.forEachNode((rowNode: IRowNode, idx: number) => {
      if (bentomax_technician_ids.includes(rowNode.data.user_id)) {
        const form_key = this.createProjectTechnicianKey("selected_input", rowNode);
        form.controls[form_key].setValue(true);
        if (form.untouched) form.markAsTouched();
      }
    });
  }

  saveProjectTechnicianFormState() {
    const form = this.projectTechnicianForm;

    const pt_promises: Promise<FullProjectTechnician>[] = [];

    this.projectTechnicianGridApi.forEachNode((rowNode: IRowNode, idx: number) => {
      const user_id = rowNode.data.user_id;

      if (user_id) {
        const project_technician = this.project.project_technicians.find((pt) => pt.user_id == user_id);

        const is_pt_form_key = this.createProjectTechnicianKey("selected_input", rowNode);
        const is_pt_value = form.controls[is_pt_form_key].value;

        const bin_id_form_key = this.createProjectTechnicianKey("bin_input", rowNode);
        const current_bin_id: number = form.controls[bin_id_form_key].value;

        if (is_pt_value && !project_technician) {
          // case is pt now, wasn't pt before
          pt_promises.push(
            new Promise<FullProjectTechnician>((resolve) => {
              this.projectTechnicianService
                .createProjectTechnician({
                  project_id: this.project.id,
                  bin_id: current_bin_id,
                  user_id: user_id,
                })
                .subscribe(
                  (data) => {
                    resolve(data);
                  },
                  (error) => {
                    console.log(error);
                    resolve(undefined);
                  },
                );
            }),
          );
        } else if (!is_pt_value && project_technician) {
          // case was pt before, isn't pt now
          pt_promises.push(
            new Promise<FullProjectTechnician>((resolve) => {
              this.projectTechnicianService.deleteProjectTechnician(project_technician.id).subscribe(
                (_) => {
                  resolve(undefined);
                },
                (error) => {
                  console.log(error);
                  resolve(undefined);
                },
              );
            }),
          );
        } else if (is_pt_value && project_technician) {
          // case was pt before, is pt now -> check if pt-bin changed
          if (project_technician.bin_id != current_bin_id) {
            pt_promises.push(
              new Promise<FullProjectTechnician>((resolve) => {
                this.projectTechnicianService
                  .updateProjectTechnician(project_technician.id, { bin_id: current_bin_id })
                  .subscribe(
                    (data) => {
                      resolve(data);
                    },
                    (error) => {
                      console.log(error);
                      resolve(undefined);
                    },
                  );
              }),
            );
          }
        }
      }
    });

    // wait for all promises
    Promise.all(pt_promises).then((_) => {
      // reset tab
      this.selectedTab = "data";

      // then reload project
      this.loadProject();
    });
  }

  async saveStoreFormState() {
    const form = this.projectStoreForm;
    const ops: { type: "create" | "delete"; store_id: number }[] = [];

    // 1. Änderungs-Set aus den aktuell vorhandenen Controls bauen
    Object.keys(form.controls).forEach((key) => {
      // key Format: "<column_field>;<store_id>"
      const [field, storeIdStr] = key.split(";");
      if (field !== "selected_input") return; // nur Checkbox-Spalte ist relevant
      const store_id = Number(storeIdStr);

      const is_ps_value = form.controls[key].value; // true/false = soll verknüpft sein
      const has_project_store = this.project.store_ids.includes(store_id); // aktuell im Projekt?

      if (is_ps_value && !has_project_store) {
        // neu verknüpfen
        ops.push({ type: "create", store_id });
      } else if (!is_ps_value && has_project_store) {
        // entknüpfen
        ops.push({ type: "delete", store_id });
      }
    });

    // Wenn nichts zu tun ist: einfach raus
    if (ops.length === 0) {
      this.selectedTab = "data";
      return;
    }

    // 2. Fortschrittsanzeige vorbereiten
    this.isSavingStores = true;
    this.storeSaveProgress = 0;
    this.storeSaveTotal = ops.length;

    // 3. Requests nacheinander ausführen und Fortschritt hochzählen
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];

      if (op.type === "create") {
        await new Promise((resolve) => {
          this.projectStoreService
            .createProjectStore({
              store_id: op.store_id,
              project_stores_id: this.project.id,
            })
            .subscribe(
              (_) => resolve(true),
              (err) => {
                console.log(err);
                resolve(false);
              },
            );
        });
      } else if (op.type === "delete") {
        await new Promise((resolve) => {
          this.projectStoreService.deleteProjectStore(this.project.id, op.store_id).subscribe(
            (_) => resolve(true),
            (err) => {
              console.log(err);
              resolve(false);
            },
          );
        });
      }

      // Fortschritt nach jedem erledigten Request
      this.storeSaveProgress = i + 1;
    }

    // 4. UI aktualisieren
    // Tab zurücksetzen
    this.selectedTab = "data";

    // Projekt frisch laden (damit store_ids stimmt)
    await this.loadProject();

    // sichtbare Controls updaten
    this.syncVisibleStoreFormControls();

    // 5. Fortschrittsanzeige ausblenden
    this.isSavingStores = false;
  }

  private generateStoreFormCellInputs(): void {
    if (this.project) {
      console.log("Gen form cell inputs");
      this.projectStoreFormCellInputs = this.customerStores.map((s) => {
        return {
          store: s,
          selected_input: {
            type: "checkbox",
            value: this.project.store_ids.includes(s.id),
          },
        };
      });
    }
  }

  async saveEditorjsData(data: OutputData, draft_id: string): Promise<boolean> {
    return await new Promise<boolean>((resolve) => {
      this.projectService.updateEditorPageContent(this.project.id, this.shownEditorPage.id, data, draft_id).subscribe(
        (data) => {
          this.updateProjectData(data);
          resolve(true);
        },
        (error) => {
          console.log(error);
          resolve(false);
        },
      );
    });
  }

  onEditorJsSave(event: { data: OutputData; draft_id: string }) {
    this.saveEditorjsData(event.data, event.draft_id);
  }

  selectEditorPage(page: EditorPage) {
    this.shownEditorPage = page;
    this.editEditorPages = false;
  }

  showEditEditorPages() {
    this.shownEditorPage = null;
    this.editEditorPages = true;
  }

  private reindex(): void {
    this.project.editor_pages = this.project.editor_pages.map((p, idx) => ({ ...p, position: idx }));
  }

  moveUp(i: number): void {
    if (i <= 0) return;
    const arr = [...this.project.editor_pages];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    this.project.editor_pages = arr;
    this.reindex();
    this.updatePages([this.project.editor_pages[i], this.project.editor_pages[i - 1]]);
  }

  moveDown(i: number): void {
    const arr = [...this.project.editor_pages];
    if (i >= arr.length - 1) return;
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    this.project.editor_pages = arr;
    this.reindex();
    this.updatePages([this.project.editor_pages[i], this.project.editor_pages[i + 1]]);
  }

  updatePages(pages: EditorPage[]) {
    this.projectService
      .updateEditorPages(
        this.project.id,
        pages.map((p) => {
          return {
            name: p.name,
            id: p.id,
            project_id: p.project_id,
            position: p.position,
          };
        }),
      )
      .subscribe(
        (data) => {
          this.updateProjectData(data);
        },
        (error) => {
          console.log(error);
        },
      );
  }

  deletePageInDB(page: EditorPage) {
    this.projectService.deleteEditorPage(this.project.id, page.id).subscribe(
      (data) => {
        this.updateProjectData(data);
      },
      (error) => {
        console.log(error);
      },
    );
  }

  renamePage(i: number, newName: string): void {
    const arr = [...this.project.editor_pages];
    arr[i] = { ...arr[i], name: (newName ?? "").trim() };
    this.updatePages([arr[i]]);
  }

  addPage(): void {
    const nextIndex = (this.project.editor_pages?.length ?? 0) + 1;
    const newPage: EditorPageCreateParams = {
      name: `Seite ${nextIndex}`,
      data: { blocks: [] },
      project_id: this.project.id,
      position: nextIndex - 1,
    };
    this.projectService.addEditorPage(this.project.id, newPage).subscribe(
      (res) => {
        this.updateProjectData(res);
      },
      (error) => {
        console.log(error);
      },
    );
  }

  openDeletePageDialog(i: number): void {
    if (confirm("Möchten Sie diese Seite wirklich löschen?")) {
      const arr = [...this.project.editor_pages];
      const deleted_pages = arr.splice(i, 1);
      this.deletePageInDB(deleted_pages[0]);
      this.project.editor_pages = arr;
      this.reindex();

      this.updatePages(arr);
    }
  }

  protected readonly faPlus = faPlus;
  protected readonly faEdit = faEdit;
  protected readonly faCog = faCog;

  openLogoUpload() {
    this.fileInput.nativeElement.click();
  }

  onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      this.saveLogo(base64String);
    };

    reader.readAsDataURL(file);
  }

  saveLogo(logo: string) {
    this.saveProjectValues({ logo });
  }

  async deleteMaterialRequirement(material_requirement: MaterialRequirement) {
    return new Promise<boolean>((resolve) => {
      this.materialRequirementService.destroyMaterialRequirement(material_requirement.id).subscribe(
        () => {
          resolve(true);
          this.onMaterialRequirementDestroyed(material_requirement.id);
        },
        (error: string) => {
          resolve(false);
          console.log(error);
        },
      );
    });
  }

  openDeleteMaterialRequirementDialog(material_requirement: MaterialRequirement) {
    if (confirm(`Möchten Sie die Materialanforderung wirklich löschen?`)) {
      this.deleteMaterialRequirement(material_requirement).then((success) => {
        if (!success) {
          alert("Fehler beim Löschen");
        }
      });
    }
  }

  private createMaterialRequirementForm(material_requirement?: MaterialRequirement): UntypedFormGroup {
    const form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      project_id: new UntypedFormControl({ value: "", disabled: true }),
      amount: new UntypedFormControl({ value: "" }, Validators.required),
      article_template_id: new UntypedFormControl({ value: "" }, Validators.required),
    });

    form.controls["id"].setValue(material_requirement?.id || null);
    form.controls["project_id"].setValue(material_requirement?.project_id || this.project.id);
    form.controls["amount"].setValue(material_requirement?.amount || 1);
    form.controls["article_template_id"].setValue(material_requirement?.article_template.id || null);

    form.updateValueAndValidity();

    return form;
  }

  onMaterialRequirementCreated(material_requirement: MaterialRequirement) {
    this.project.material_requirements.push(material_requirement);
    this.updateMaterialRequirementData();
  }

  onMaterialRequirementUpdated(material_requirement: MaterialRequirement) {
    const updated_idx = this.project.material_requirements.findIndex((mr) => mr.id == material_requirement.id);
    this.project.material_requirements[updated_idx] = material_requirement;
    this.updateMaterialRequirementData();
  }

  onMaterialRequirementDestroyed(material_requirement_id: number) {
    const updated_idx = this.project.material_requirements.findIndex((mr) => mr.id == material_requirement_id);
    this.project.material_requirements.splice(updated_idx, 1);
    this.updateMaterialRequirementData();
  }

  async activateAllProjectStores() {
    const confirmed = confirm("Wollen Sie wirklich alle Filialen aktivieren?");
    if (!confirmed) return;

    // Nur Filialen finden, die noch NICHT verknüpft sind
    const unlinkedStores = this.projectStoreFormCellInputs
      .filter((cell) => !this.project.store_ids.includes(cell.store.id))
      .map((cell) => cell.store.id);

    if (unlinkedStores.length === 0) {
      alert("Alle Filialen sind bereits aktiviert.");
      return;
    }

    // Fortschrittsbalken aktivieren
    this.isSavingStores = true;
    this.storeSaveProgress = 0;
    this.storeSaveTotal = unlinkedStores.length;

    // Jetzt nacheinander verknüpfen
    for (let i = 0; i < unlinkedStores.length; i++) {
      const storeId = unlinkedStores[i];
      try {
        await new Promise((resolve) => {
          this.projectStoreService
            .createProjectStore({
              store_id: storeId,
              project_stores_id: this.project.id,
            })
            .subscribe(
              (_) => resolve(true),
              (err) => {
                console.error(err);
                resolve(false);
              },
            );
        });
      } catch (e) {
        console.error("Fehler bei Filiale", storeId, e);
      }

      this.storeSaveProgress = i + 1;
    }

    this.selectedTab = "data";

    this.isSavingStores = false;

    alert("Alle Filialen wurden aktiviert.");

    // Nach Abschluss → Projekt neu laden
    await this.loadProject();

    // 🧹 Alle FormControls löschen
    const formGroup = this.projectStoreForm;
    Object.keys(formGroup.controls).forEach((key) => formGroup.removeControl(key));
  }
}
